import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { ErrorState } from '../ui/ErrorState';
import { changeDriver } from '../../features/deliveries/deliveriesThunks';
import { fetchDrivers } from '../../features/orderDetails/orderDetailsThunks';

/**
 * Hands a batch of shipments to one driver.
 *
 * The admin's fleet is its own; the staff's is the operator picked in the
 * filters (`adminId`), because the backend refuses a driver of one operator for
 * a shipment of another. How many were assigned is said by the toast of
 * `changeDriver`; a failure is said here, and the dialog stays open.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {number[]} props.selectedDeliveries - Order ids, which is what the
 *   endpoint matches on.
 * @param {() => void} [props.onSuccess]
 * @param {number|string} [props.adminId] - Staff only: the operator being looked at.
 * @param {string} [props.adminLabel] - Its name.
 */
export const ChangeDriverModal = ({
  isOpen,
  onClose,
  selectedDeliveries,
  onSuccess,
  adminId,
  adminLabel,
}) => {
  const dispatch = useDispatch();
  const { driverOptions, driversLoaded, driversError } = useSelector((state) => state.orderDetails);
  const [driverId, setDriverId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const loadDrivers = () => dispatch(fetchDrivers(adminId ? { adminId } : undefined));

  // Re-asked on every opening for the staff: the list in the store belongs to
  // whichever operator was being looked at before.
  useEffect(() => {
    if (isOpen) dispatch(fetchDrivers(adminId ? { adminId } : undefined));
  }, [isOpen, adminId, dispatch]);

  const handleClose = () => {
    if (saving) return;
    setDriverId('');
    setError(null);
    onClose();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!driverOptions.some((driver) => String(driver.value) === String(driverId))) return;

    setSaving(true);
    setError(null);
    const result = await dispatch(changeDriver({ ids: selectedDeliveries, driver_id: Number(driverId) }));
    setSaving(false);

    // No answer: it may have been applied, which the page's warning says. The
    // table is refreshed to show what happened, and nothing invites a retry.
    if (changeDriver.fulfilled.match(result) || result.meta?.unconfirmed) {
      setDriverId('');
      onSuccess?.();
      onClose();
      return;
    }
    setError(typeof result.payload === 'string' ? result.payload : 'No se pudo asignar el conductor');
  };

  const count = selectedDeliveries.length;
  // A pick from a list that was replaced (another operator's) is no pick.
  const chosen = driverOptions.some((driver) => String(driver.value) === String(driverId));
  const placeholder = !driversLoaded
    ? 'Cargando conductores...'
    : driverOptions.length === 0
      ? 'No hay conductores registrados'
      : 'Seleccionar conductor';

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Asignar conductor"
      size="sm"
      testId="change-driver-modal"
      busy={saving}
      error={error}
      errorTitle="No se pudo asignar"
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        <div className="rounded-xl bg-ninja-gray dark:bg-[#222] p-4 space-y-1">
          <p className="text-sm text-text-secondary">
            Se {count === 1 ? 'reasignará' : 'reasignarán'}{' '}
            <strong className="text-ninja-dark dark:text-white">{count}</strong>{' '}
            {count === 1 ? 'envío seleccionado' : 'envíos seleccionados'}. Los finalizados y los de otro
            operador no cambian.
          </p>
          {adminLabel && (
            <p className="text-xs text-text-muted">
              Conductores de <strong className="text-ninja-dark dark:text-white">{adminLabel}</strong>,
              que es el operador que estás mirando.
            </p>
          )}
        </div>

        {driversError && !driversLoaded ? (
          <ErrorState
            variant="inline"
            title="No se pudieron cargar los conductores"
            description={driversError}
            onRetry={loadDrivers}
          />
        ) : (
          <div>
            <label className="block text-sm font-semibold text-ninja-dark dark:text-white mb-2">
              Conductor
            </label>
            <select
              value={chosen ? driverId : ''}
              onChange={(event) => setDriverId(event.target.value)}
              required
              disabled={!driversLoaded || driverOptions.length === 0 || saving}
              data-testid="change-driver-select"
              className="w-full bg-ninja-gray dark:bg-[#111] border border-border rounded-xl px-4 py-3 text-sm text-ninja-dark dark:text-white focus:outline-none focus:ring-2 focus:ring-ninja-green transition-all disabled:opacity-60"
            >
              <option value="" disabled>
                {placeholder}
              </option>
              {driverOptions.map((driver) => (
                <option key={driver.value} value={driver.value}>
                  {driver.label}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="flex items-center justify-end gap-3">
          <Button variant="subtle" pill onClick={handleClose} disabled={saving}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            pill
            loading={saving}
            loadingLabel="Asignando..."
            disabled={!chosen}
          >
            Asignar
          </Button>
        </div>
      </form>
    </Modal>
  );
};
